import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IoIosArrowForward } from 'react-icons/io';
import { redirectApp } from '../../../core/utils/helper';
import { INSPIRATION_SLIDES } from '../../components/data/inspirationSlides';

const SLIDES = INSPIRATION_SLIDES;
const SLIDE_COUNT = SLIDES.length;
const SLIDE_DURATION = 5000;
const GAP = 16;

function getVisibleCount() {
  if (typeof window === 'undefined') return 3;
  if (window.innerWidth >= 1024) return 3;
  if (window.innerWidth >= 640) return 2;
  return 1;
}

function GalleryItem({ slide, width }) {
  const slideStyle =
    width > 0
      ? { width, minWidth: width, flex: `0 0 ${width}px` }
      : { flex: '0 0 100%' };

  return (
    <div className="inspiration-slide select-none" style={slideStyle}>
      <div
        className="overflow-hidden rounded-2xl hover:scale-[1.02] transition-all duration-300" 
      >
        <img
          src={slide.image}
          alt={slide.caption}
          className="block w-full h-full object-cover pointer-events-none min-h-[250px] max-h-[250px] lg:min-h-[370px] lg:max-h-[370px]"
          draggable={false}
        />
      </div>
      <p className="mt-5 text-[16px]text-black font-inter text-center">
        {slide.caption}
      </p>
    </div>
  );
}

export default function ExploreSlider() {
  const containerRef = useRef(null);
  const [itemWidth, setItemWidth] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const [trackIndex, setTrackIndex] = useState(SLIDE_COUNT);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  const progressElapsedRef = useRef(0);
  const rafRef = useRef(null);
  const dragStartRef = useRef({ x: 0, pointerId: null });
  const isDraggingRef = useRef(false);
  const isHoveredRef = useRef(false);

  const trackSlides = useMemo(
    () => [...SLIDES, ...SLIDES, ...SLIDES],
    [],
  );

  const slideStep = itemWidth > 0 ? itemWidth + GAP : 0;
  const baseTranslate = slideStep > 0 ? trackIndex * slideStep : 0;

  const measureItems = useCallback(() => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    if (containerWidth <= 0) return;

    const count = getVisibleCount();
    const width = (containerWidth - GAP * (count - 1)) / count;
    if (width > 0) {
      setItemWidth(width);
    }
  }, []);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    measureItems();

    const observer = new ResizeObserver(() => {
      measureItems();
    });
    observer.observe(node);

    window.addEventListener('resize', measureItems);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measureItems);
    };
  }, [measureItems]);

  const resetProgress = useCallback(() => {
    progressElapsedRef.current = 0;
    setProgress(0);
  }, []);

  const goToNext = useCallback(() => {
    setActiveSlide((prev) => (prev + 1) % SLIDE_COUNT);
    setTrackIndex((prev) => prev + 1);
    resetProgress();
  }, [resetProgress]);

  const goToPrev = useCallback(() => {
    setActiveSlide((prev) => (prev - 1 + SLIDE_COUNT) % SLIDE_COUNT);
    setTrackIndex((prev) => prev - 1);
    resetProgress();
  }, [resetProgress]);

  useEffect(() => {
    if (isPaused || isDragging || slideStep === 0) return undefined;

    let startTime = null;

    const tick = (timestamp) => {
      if (startTime === null) {
        startTime = timestamp - progressElapsedRef.current;
      }

      const FAST_FACTOR = 0.5; 
      const effectiveDuration = SLIDE_DURATION * FAST_FACTOR;

      const elapsed = timestamp - startTime;
      const pct = Math.min((elapsed / effectiveDuration) * 100, 100);
      progressElapsedRef.current = elapsed;
      setProgress(pct);

      if (pct >= 100) {
        goToNext();
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [activeSlide, isPaused, isDragging, slideStep, goToNext]);

  const handleTransitionEnd = () => {
    if (trackIndex >= SLIDE_COUNT * 2) {
      setIsTransitioning(false);
      setTrackIndex((prev) => prev - SLIDE_COUNT);
    } else if (trackIndex < SLIDE_COUNT) {
      setIsTransitioning(false);
      setTrackIndex((prev) => prev + SLIDE_COUNT);
    }
  };

  useEffect(() => {
    if (!isTransitioning) {
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsTransitioning(true));
      });
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [isTransitioning]);

  const handlePause = () => {
    isHoveredRef.current = true;
    setIsPaused(true);
  };

  const handleResume = () => {
    isHoveredRef.current = false;
    if (!isDraggingRef.current) setIsPaused(false);
  };

  const handlePointerDown = (event) => {
    if (slideStep === 0) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    dragStartRef.current = { x: event.clientX, pointerId: event.pointerId };
    isDraggingRef.current = true;
    setIsDragging(true);
    setIsPaused(true);
    setDragOffset(0);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!isDraggingRef.current) return;
    const delta = event.clientX - dragStartRef.current.x;
    setDragOffset(delta);
  };

  const finishDrag = (event) => {
    if (!isDraggingRef.current) return;

    const delta = event.clientX - dragStartRef.current.x;
    const threshold = slideStep / 4;

    if (delta < -threshold) {
      goToNext();
    } else if (delta > threshold) {
      goToPrev();
    } else {
      resetProgress();
    }

    setDragOffset(0);
    isDraggingRef.current = false;
    setIsDragging(false);
    setIsPaused(isHoveredRef.current);

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <section
      id="use-cases"
      className="py-16 sm:py-20 lg:py-24 bg-white px-2"
    >
      <div className="max-w-[1170px] mx-auto flex flex-col items-center text-center">
        <h2 className="text-black lg:text-[48px] text-[32px] font-semibold font-inter">
          Explore and get inspired
        </h2>
        <p className="mt-[10px] text-gray-600 text-[16px] font-normal font-inter max-w-2xl">
          Discover the platforms, products, and innovations shaping the AI-PRO
          experience.
        </p>
        <button
          type="button"
          onClick={(e) => redirectApp(e, 'registration')}
          className="mt-6 flex items-center justify-center text-base bg-[#3073D5] hover:bg-[#2590EE] text-white pl-[16px] pr-[12px] py-[10px] rounded-full transition"
        >
          Try Now <IoIosArrowForward className="ml-1 text-base" />
        </button>
      </div>

      <div
        ref={containerRef}
        className="pt-12 w-full mx-auto overflow-hidden touch-pan-y"
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        onMouseEnter={handlePause}
        onMouseLeave={() => {
          if (isDraggingRef.current) return;
          handleResume();
        }}
      >
        <div
          className={`flex will-change-transform ${isTransitioning && !isDragging ? 'transition-transform duration-500 ease-out' : ''}`}
          style={{
            gap: `${GAP}px`,
            transform:
              slideStep > 0
                ? `translateX(${-(baseTranslate - dragOffset)}px)`
                : undefined,
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {trackSlides.map((slide, index) => (
            <GalleryItem
              key={`${slide.id}-${index}`}
              slide={slide}
              width={itemWidth}
            />
          ))}
        </div>
      </div>

      <div
        className="mt-10 flex items-center justify-center gap-2 pointer-events-none select-none"
        aria-hidden="true"
      >
        {Array.from({ length: SLIDE_COUNT }, (_, index) =>
          index === activeSlide ? (
            <div
              key={index}
              className="relative w-10 h-2 rounded-full bg-[#E5E5E5] overflow-hidden"
            >
              <div
                className="absolute inset-y-0 left-0 bg-[#3073D5] rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
          ) : (
            <div
              key={index}
              className="w-2 h-2 rounded-full bg-[#E5E5E5]"
            />
          ),
        )}
      </div>
    </section>
  );
}
